import { createSelector } from 'reselect';
import { array, ord, string } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { ApplicationState, WithScope } from '@mews-commander/types';
import { Selector, createAsyncMetaSelectors, createScopedSelector } from '@mews/framework';
import { Identifiable } from '@mews/std';
import {
    getAccountNotes,
    getAccountNotesByAccountId,
    getAccountNotesByIds,
} from '@mews-commander/legacy-entities/src/ducks/accountNotes';
import { getProfiles } from '@mews-commander/legacy-entities/src/ducks/profiles';
import { getSelectedEntityId } from '../entityManagement/selectors';
import { moduleSelector } from './module';
import { AccountNotesState, initialState } from './reducers';

const scopedModuleSelector: Selector<ApplicationState, WithScope, AccountNotesState> = createScopedSelector({
    parentSelector: moduleSelector,
    initialState,
});

const getModuleMeta = createSelector(scopedModuleSelector, (state) => state.meta);

export const [isInitFetching, hasInitFailed] = createAsyncMetaSelectors(getModuleMeta);

export const getSelectedAccountNote = createSelector(getAccountNotes, getSelectedEntityId, (notes, id) => notes[id]);

export const getSelectedAccountNoteFormData = createSelector(getSelectedAccountNote, (note) => {
    if (!note) return;

    const { data, content } = note;
    const classification = data?.value?.classifications[0];

    return {
        content,
        classification,
    };
});

export const getAccountNotesTableData = createSelector(getAccountNotesByIds, getProfiles, (notes, profiles) =>
    pipe(
        notes,
        array.map((note) => ({
            id: note.id,
            content: note.content,
            discriminator: note.data.discriminator,
            classification: note.data.value.classifications[0],
            updaterProfileName: profiles?.[note.updaterProfileId]?.name,
            createdUtc: note.createdUtc,
            updatedUtc: note.updatedUtc,
        }))
    )
);

interface SortingData extends Identifiable {
    updatedUtc: string;
}

const byUpdatedUtc: ord.Ord<SortingData> = pipe(
    string.Ord,
    ord.contramap(({ updatedUtc }) => updatedUtc)
);

export const getSortedAccountNoteIdsByAccountId = flow(
    getAccountNotesByAccountId,
    array.map((note) => ({
        id: note.id,
        updatedUtc: note.updatedUtc,
    })),
    array.sort(byUpdatedUtc),
    array.map(({ id }) => id),
    array.reverse
);
